'use client';

import { useState } from 'react';
import { useTranslation } from '@/i18n/LanguageProvider';
import Link from '@/components/LocaleLink';
import { ChevronDown, ChevronRight, Headphones, Map } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
}

function FAQAccordion({ items }: { items: FAQItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return (
    <div className="space-y-4">
      {items.map((item, index) => (
        <div key={index} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
          <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full text-left px-6 py-4 flex items-center justify-between gap-4">
            <span className="font-semibold text-gray-900 dark:text-gray-100">{item.question}</span>
            <ChevronDown className={`w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform ${openIndex === index ? 'rotate-180' : ''}`} />
          </button>
          {openIndex === index && <div className="px-6 pb-4 text-gray-600 dark:text-gray-300">{item.answer}</div>}
        </div>
      ))}
    </div>
  );
}

export default function FAQPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'travelers' | 'guides'>('travelers');

  const travelerFAQs: FAQItem[] = [
    { question: t('faq.t1Q'), answer: t('faq.t1A') },
    { question: t('faq.t2Q'), answer: t('faq.t2A') },
    { question: t('faq.t3Q'), answer: t('faq.t3A') },
    { question: t('faq.t4Q'), answer: t('faq.t4A') },
    { question: t('faq.t5Q'), answer: t('faq.t5A') },
    { question: t('faq.t6Q'), answer: t('faq.t6A') },
    { question: t('faq.t7Q'), answer: t('faq.t7A') },
    { question: t('faq.t8Q'), answer: t('faq.t8A') },
    { question: t('faq.t9Q'), answer: t('faq.t9A') },
  ];

  const guideFAQs: FAQItem[] = [
    { question: t('faq.g1Q'), answer: t('faq.g1A') },
    { question: t('faq.g2Q'), answer: t('faq.g2A') },
    { question: t('faq.g3Q'), answer: t('faq.g3A') },
    { question: t('faq.g4Q'), answer: t('faq.g4A') },
    { question: t('faq.g5Q'), answer: t('faq.g5A') },
    { question: t('faq.g6Q'), answer: t('faq.g6A') },
  ];

  return (
    <div>
      <section className="bg-gradient-to-br from-primary-600 to-secondary-600 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('faq.title')}</h1>
          <p className="text-xl text-white/90">{t('faq.subtitle')}</p>
        </div>
      </section>

      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
              <button onClick={() => setActiveTab('travelers')} className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${activeTab === 'travelers' ? 'bg-white dark:bg-gray-700 text-primary shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'}`}>
                <Map className="w-5 h-5" />{t('faq.travelersTab')}
              </button>
              <button onClick={() => setActiveTab('guides')} className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${activeTab === 'guides' ? 'bg-white dark:bg-gray-700 text-primary shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'}`}>
                <Headphones className="w-5 h-5" />{t('faq.guidesTab')}
              </button>
            </div>
          </div>

          {activeTab === 'travelers' ? <FAQAccordion items={travelerFAQs} /> : <FAQAccordion items={guideFAQs} />}

          <div className="mt-16 text-center bg-gray-50 dark:bg-gray-800 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('faq.stillQuestions')}</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">{t('faq.contactUs')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/contact" className="btn-primary inline-flex items-center justify-center">{t('footer.contact')} <ChevronRight className="ml-2 w-5 h-5" /></Link>
              <a href="mailto:soporte@iterogo.com" className="btn-outline inline-flex items-center justify-center">soporte@iterogo.com</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
